import { Reference } from '@approvalmax/types';
import {
    Button,
    Checkbox,
    CheckboxEditor,
    DropdownEditor,
    FocusImplementer,
    NumberEditor,
    TextAreaEditor,
} from '@approvalmax/ui';
import { Button as NewButton } from '@approvalmax/ui/src/components';
import { errorHelpers, intl } from '@approvalmax/utils';
import { useAtomValue, useSetAtom } from 'jotai';
import { constants, selectors } from 'modules/common';
import { backend, domain, schemas } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useDispatch, useSelector } from 'modules/react-redux';
import { AddMatchingIcon, MatchingIcon } from 'modules/sprites';
import moment from 'moment';
import { LineItemsGroupEdit, LineItemsTable, ReadOnlyCell } from 'pages/requestForm/components';
import { memo, useCallback, useEffect, useMemo } from 'react';
import bemFactory from 'react-bem-factory';
import { usePrevious, useUpdateEffect } from 'react-use';
import {
    ComponentErrorBoundary,
    type MatchingInfoNormalizer,
    QBooksMatchingInfo,
    type QBooksMatchingInfoProps,
} from 'shared/components';
import {
    type LineItemNormalizer,
    qBooksMatchingPopupLineItemNormalizerState,
    qBooksMatchingPopupLineItemState,
} from 'shared/components/QBooksMatchingPopup';
import { ocrIsLoadingState } from 'shared/states';

import {
    addQbooksLineItem,
    changeQbooksLineAmountType,
    changeQbooksLineItemChecked,
    changeQbooksLineItemDescription,
    changeQbooksLineItemQty,
    changeQbooksLineItemsAllChecked,
    changeQbooksLineItemsClass,
    changeQbooksLineItemsCustomer,
    changeQbooksLineItemServiceDate,
    changeQbooksLineItemsItem,
    changeQbooksLineItemsTaxCode,
    changeQbooksLineItemUnitPrice,
    cloneQbooksLineItems,
    removeQbooksLineItems,
    reorderQbooksLineItems,
    toggleQbooksLineItemIsBillable,
    toggleQbooksLineItemIsTaxable,
} from '../../../actions';
import { getClassesWithNesting, QBooksClass } from '../../../data/qbooks/QBooksClass';
import { findLineAmountType, lineAmountTypeReferences } from '../../../data/qbooks/statics/lineAmountType';
import { getContext, getShowValidationErrors, requestSelector } from '../../../selectors/pageSelectors';
import { getQBooksAccountLineItems, getQBooksContext, getQBooksLineItemsV2 } from '../../../selectors/qbooks';
import { getTaxCodeList } from '../../../selectors/qbooks/taxSelectors';
import { getRequestEditMode, getRequiredFields, RequestEditMode } from '../../../selectors/requestSelectors';
import { ColumnDefinition } from '../../../selectors/types/ColumnDefinition';
import { ExpandedQBooksLineItem } from '../../../selectors/types/ExpandedQBooksLineItem';
import { defaultOnChangeFieldRequiredForMatching } from '../QBooksBillForm/QBooksBillForm.helpers';
import type { OnChangeFieldRequiredForMatching } from '../QBooksBillForm/QBooksBillForm.types';
import { useConstructLineItem, useGroupEditItems } from './QBooksLineItemsSection.hooks';
import { messages } from './QBooksLineItemsSection.messages';
import {
    AmountCell,
    BillableCheckbox,
    Content,
    GroupActions,
    Header,
    HeaderFields,
    HeaderFieldsLabel,
    HeaderFieldsLineAmountTypeContainer,
    Linked,
    PriceIndicator,
    QBooksLineItemCustomerCellStyled,
    QbooksLineItemDropdownCellStyled,
    QbooksLineItemItemCellStyled,
    Root,
    ServiceDateCell,
    StyledPriceCheckIndicator,
    TaxableCheckbox,
} from './QBooksLineItemsSection.styles';

const qa = bemFactory.qa('line-items-section');

interface QBooksLineItemsSectionProps extends Partial<Pick<QBooksMatchingInfoProps, 'onRemoveMatching'>> {
    onChange?: OnChangeFieldRequiredForMatching;
}

export const QBooksLineItemsSection = memo<QBooksLineItemsSectionProps>((props) => {
    const { onChange = defaultOnChangeFieldRequiredForMatching, onRemoveMatching } = props;

    const dispatch = useDispatch();
    const qbooksContext = useSelector(getQBooksContext);
    const setLineItem = useSetAtom(qBooksMatchingPopupLineItemState);
    const setLineItemNormalizer = useSetAtom(qBooksMatchingPopupLineItemNormalizerState);
    const baseRequest = useSelector(requestSelector);
    const request = useSelector((state) => selectors.request.expandRequest(state, baseRequest)) as
        | selectors.types.QBooksBillExpandedRequest
        | selectors.types.QBooksPoExpandedRequest
        | selectors.types.QBooksExpenseExpandedRequest
        | selectors.types.QBooksInvoiceExpandedRequest;

    const { details } = request;
    const { id: profileId } = useSelector(selectors.profile.getProfileUser);
    const accountLineItems = useSelector((state) => getQBooksAccountLineItems(state, request));
    const showValidationErrors = useSelector(getShowValidationErrors);
    const isSalesInvoice = request.integrationCode === domain.IntegrationCode.QBooksInvoice;
    const hasOtherLineItems = !isSalesInvoice ? accountLineItems.some(({ empty }) => !empty) : false;
    const requiredFields = useSelector((state) => getRequiredFields(state, request));
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const isApproverMode = editMode === RequestEditMode.Approver;

    const context = useSelector(getContext);

    const company = useSelector((state) => selectors.company.getCompanyById(state, request.companyId));
    const priceCheckerSettings = company.qboPriceCheckerSettings;
    const manualMatchingEnabled = company.billMatchingSettings?.manualMatchingEnabled || false;
    const showManualMatching = manualMatchingEnabled && selectors.company.getQBOMatchingIsAvailable(company);
    const billsRequiredDataIncomplete = !('vendor' in details) || !details.vendor?.id || !details.date;
    const noMatchingPossible = !showManualMatching || billsRequiredDataIncomplete;

    const isUSOrg = company?.integration?.countryCode === backend.OrganisationVersion.QBO_US;
    const isMultiCurrency = request.companyCurrency !== request.currency;
    const isQBOMatchingOnEditingBetaSupported = company.betaFeatures.includes(
        domain.CompanyBetaFeature.QBOMatchingOnEditing
    );
    const { isActiveReviewer, isAuthor } = request.flags;
    const canDoMatching = isActiveReviewer || isAuthor;

    const taxCodesList = getTaxCodeList(
        qbooksContext,
        details.date,
        isSalesInvoice ? domain.TaxApplicableOnType.Sales : undefined
    );

    const selectLineItems = useMemo(() => getQBooksLineItemsV2(request), [request]);
    const lineItems = useSelector(selectLineItems);

    const qbooksItemReadonly = useMemo(() => {
        const qbooksItemField = context.fields.find((field) =>
            isSalesInvoice
                ? field.systemPurpose === domain.FieldSystemPurpose.QBooksSalesItem
                : field.systemPurpose === domain.FieldSystemPurpose.QBooksPoItem
        );

        return Boolean(qbooksItemField && context.readonlyFieldIds.includes(qbooksItemField.id));
    }, [context.fields, context.readonlyFieldIds, isSalesInvoice]);

    const { constructLineItem } = useConstructLineItem(request, lineItems);

    const {
        isGroupCloneVisible,
        isGroupEditVisible,
        isGroupRemoveVisible,
        isShowGroupButtons,
        openGroupEdit,
        handleGroupClone,
        handleGroupRemove,
        isGroupEditPopupVisible,
        groupItems,
        closeGroupEdit,
    } = useGroupEditItems({
        lineItems,
        taxCodesList,
        itemVisible: !qbooksItemReadonly,
        taxVisible: request.details.lineAmountType !== domain.LineAmountType.NoTax,
        classVisible: qbooksContext.hasClassFeature,
        customerVisible: !isSalesInvoice && qbooksContext.hasCustomerFeature,
    });

    const ocrIsLoading = useAtomValue(ocrIsLoadingState);

    const amountSumPerItem = useMemo(
        () =>
            lineItems.reduce<Record<string, number>>((acc, cur) => {
                if (cur.matchedLineItemId) {
                    acc[cur.matchedLineItemId] = (acc[cur.matchedLineItemId] || 0) + (cur.computedAmount ?? 0);
                }

                return acc;
            }, {}),
        [lineItems]
    );

    const lineItemNormalizer = useCallback<LineItemNormalizer<ReturnType<typeof schemas.mapLineItem>>>(
        (value) => {
            return {
                ...value,
                remainingBalanceOrig: value.remainingBalance,
                remainingBalance: (value.remainingBalanceForRequest || 0) - (amountSumPerItem?.[value.id] || 0),
            };
        },
        [amountSumPerItem]
    );

    const matchingInfoNormalizer = useCallback<MatchingInfoNormalizer>(
        async (value, lineItemId) => {
            if (!isQBOMatchingOnEditingBetaSupported) {
                return value;
            }

            // a case:
            // - initially we have an unmatched line.
            // - we match it w/ some PO
            // - we see that the icon changed to "matched", which can be used
            // to display a popup w/ matching info
            // - we are open the popup and AT THIS POINT BE don't know about
            // the fact that locally line was matched
            //
            // Thus, our goal here is to fill the matched line from by the data,
            // we've gathered
            const gatheredValue = value?.id ? value : await constructLineItem(lineItemId);

            if (!gatheredValue || !gatheredValue.lineItem) {
                throw errorHelpers.notFoundError();
            }

            return {
                ...gatheredValue,
                lineItem: lineItemNormalizer(gatheredValue.lineItem),
            };
        },
        [lineItemNormalizer, constructLineItem, isQBOMatchingOnEditingBetaSupported]
    );

    useEffect(() => {
        setLineItemNormalizer(lineItemNormalizer);
    }, [lineItemNormalizer, setLineItemNormalizer]);

    const classes = useMemo(() => getClassesWithNesting(qbooksContext.classes), [qbooksContext.classes]);

    const tableDef = useMemo(() => {
        const isBill = request.integrationCode === domain.IntegrationCode.QBooksBill;
        const isExpense = request.integrationCode === domain.IntegrationCode.QBooksExpense;
        const isPO = request.integrationCode === domain.IntegrationCode.QBooksPo;

        const classColMandatory =
            qbooksContext.hasClassFeature &&
            qbooksContext.classField &&
            requiredFields.fieldIds.includes(qbooksContext.classField.id);

        const customerField = context.fields.find((f) => f.systemPurpose === domain.FieldSystemPurpose.QBooksCustomer);

        const customerColMandatory =
            qbooksContext.hasCustomerFeature && customerField && requiredFields.fieldIds.includes(customerField.id);

        const isPriceCheckerEnabled =
            (isBill && priceCheckerSettings?.isEnabledForBills) ||
            (isExpense && priceCheckerSettings?.isEnabledForExpenses) ||
            (isPO && priceCheckerSettings?.isEnabledForPurchaseOrders);

        const isServiceDateEnabled = isSalesInvoice && qbooksContext.salesFormOptionalFields?.serviceDateEnabled;

        return [
            ...(isApproverMode
                ? []
                : [
                      {
                          id: 'checkbox',
                          name: '',
                          fixedWidth: 40,
                          headerCell: (lineItems) => {
                              const checked = lineItems.every((li) => li.checked);
                              const triState = lineItems.some((li) => li.checked);

                              const handleClick = () => {
                                  const nextState = !checked;

                                  dispatch(changeQbooksLineItemsAllChecked(nextState));
                              };

                              return (
                                  <div onClick={handleClick}>
                                      <Checkbox size='size-18' triState={triState} checked={checked} />
                                  </div>
                              );
                          },
                          cell: (li) => {
                              return (
                                  <div
                                      onClick={() => {
                                          dispatch(changeQbooksLineItemChecked(li.id, !li.checked));
                                      }}
                                  >
                                      <Checkbox checked={li.checked} size='size-18' />
                                  </div>
                              );
                          },
                      } as ColumnDefinition<ExpandedQBooksLineItem>,
                  ]),
            ...(isServiceDateEnabled
                ? [
                      {
                          id: 'serviceDate',
                          name: messages.colNameServiceDate,
                          flexGrow: 1,
                          readonly: () => ocrIsLoading,
                          cell: (li, { readonly }) =>
                              readonly ? (
                                  <ReadOnlyCell text={li.serviceDate || ''} />
                              ) : (
                                  <FocusImplementer>
                                      {({ registerFocusable }) => (
                                          <ServiceDateCell
                                              ref={registerFocusable}
                                              value={li.serviceDate || null}
                                              onChange={(serviceDate) =>
                                                  dispatch(changeQbooksLineItemServiceDate(li.id, serviceDate))
                                              }
                                              placeholder={messages.cellPlaceholderServiceDate}
                                          />
                                      )}
                                  </FocusImplementer>
                              ),
                      } as ColumnDefinition<ExpandedQBooksLineItem>,
                  ]
                : []),
            {
                id: 'item',
                name: messages.colNameItem,
                flexGrow: 1,
                readonly: () => ocrIsLoading,
                invalid: (li) => showValidationErrors && !li.empty && !li.item,
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.item && li.item.text} />
                    ) : (
                        <QbooksLineItemItemCellStyled
                            qa={qa('cell-editor')}
                            invalid={invalid}
                            value={li.item || null}
                            onChange={(newItem: domain.QBooksItem | null) =>
                                onChange(() => dispatch(changeQbooksLineItemsItem([li.id], newItem)), {
                                    lineId: li.id,
                                }).catch(() => {})
                            }
                            placeholder={messages.cellPlaceholderItem}
                            isSalesInvoice={isSalesInvoice}
                        />
                    ),
            },
            {
                id: 'description',
                name: messages.colNameDescription,
                flexGrow: 2,
                readonly: () => ocrIsLoading,
                cell: (li, { readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.description} multiline />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <TextAreaEditor
                                    changeOnBlur
                                    theme='transparent'
                                    qa={qa('cell-editor')}
                                    ref={registerFocusable}
                                    value={li.description}
                                    maxLength={constants.qBooksConstants.LINE_ITEM_DESCRIPTION_MAX_LENGTH}
                                    placeholder={messages.cellPlaceholderDescription}
                                    onChange={(newDescription) =>
                                        dispatch(changeQbooksLineItemDescription(li.id, newDescription))
                                    }
                                />
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'qty',
                name: messages.colNameQty,
                alignRight: true,
                flexGrow: 0.5,
                readonly: () => ocrIsLoading,
                invalid: (li) => showValidationErrors && !li.empty && li.qty == null,
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.qty != null ? intl.formatNumber(li.qty, 'auto') : ''} />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <NumberEditor
                                    allowFloat
                                    allowCalculation
                                    theme='transparent'
                                    align='right'
                                    displayFormat='currency-auto'
                                    qa={qa('cell-editor')}
                                    ref={registerFocusable}
                                    invalid={invalid}
                                    precision={constants.qBooksConstants.PRECISION_BIG}
                                    min={constants.qBooksConstants.TOTAL_MIN_AMOUNT}
                                    max={constants.qBooksConstants.TOTAL_MAX_AMOUNT}
                                    placeholder={messages.cellPlaceholderQty}
                                    value={li.qty != null ? li.qty : null}
                                    onChange={(newQty: number | null) => {
                                        const unitPrice =
                                            request.details.lineAmountType === domain.LineAmountType.TaxExclusive
                                                ? li.unitPrice
                                                : li.unitPriceGross;

                                        const newAmount = (newQty ?? 0) * (unitPrice ?? 0);

                                        // additionally, skip for a new lines
                                        if (li.amount && (!newQty || li.amount < newAmount)) {
                                            onChange(() => dispatch(changeQbooksLineItemQty(li.id, newQty)), {
                                                lineId: li.id,
                                            }).catch(() => {
                                                const prevQty = li.qty !== undefined ? li.qty : null;

                                                // this is a dirty-hack because we need a way to revert a prev
                                                // state, but we can't just pass a `value` prop, because
                                                // `NumberEditor` controllable implementation is bad
                                                dispatch(changeQbooksLineItemQty(li.id, -1));
                                                queueMicrotask(() => {
                                                    dispatch(changeQbooksLineItemQty(li.id, prevQty));
                                                });
                                            });
                                        } else {
                                            dispatch(changeQbooksLineItemQty(li.id, newQty));
                                        }
                                    }}
                                />
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'unitPrice',
                name: messages.colNameUnitPrice,
                alignRight: true,
                flexGrow: 0.7,
                invalid: (li) => showValidationErrors && !li.empty && li.unitPrice == null,

                cell: (li, { invalid, readonly }) => {
                    const unitPrice =
                        request.details.lineAmountType === domain.LineAmountType.TaxExclusive
                            ? li.unitPrice
                            : li.unitPriceGross;

                    const originalPrice =
                        request.details.lineAmountType === domain.LineAmountType.TaxExclusive
                            ? li.originalUnitPrice
                            : li.originalUnitPriceGross;

                    return readonly ? (
                        <ReadOnlyCell
                            text={li.unitPrice !== undefined ? intl.formatNumber(li.unitPrice, 'auto') : ''}
                        />
                    ) : (
                        <StyledPriceCheckIndicator
                            actualPrice={unitPrice}
                            expectedPrice={originalPrice}
                            disabled={!isPriceCheckerEnabled}
                            integrationType={domain.IntegrationType.QBooks}
                            integrationCode={request.integrationCode}
                            isMultiCurrency={isMultiCurrency}
                        >
                            {({ warningLevel }) => (
                                <FocusImplementer>
                                    {({ registerFocusable }) => (
                                        <>
                                            <NumberEditor
                                                allowFloat
                                                ref={registerFocusable}
                                                invalid={invalid}
                                                value={unitPrice !== undefined ? unitPrice : null}
                                                onChange={(newValue) => {
                                                    const newAmount = (li.qty ?? 0) * (newValue ?? 0);

                                                    // additionally, skip for a new lines
                                                    if (li.amount && (!newValue || li.amount < (newAmount ?? 0))) {
                                                        onChange(
                                                            () =>
                                                                dispatch(
                                                                    changeQbooksLineItemUnitPrice(li.id, newValue)
                                                                ),
                                                            { lineId: li.id }
                                                        ).catch(() => {
                                                            const prevUnitPrice =
                                                                unitPrice !== undefined ? unitPrice : null;

                                                            // this is a dirty-hack because we need a way to revert a prev
                                                            // state, but we can't just pass a `value` prop, because
                                                            // `NumberEditor` controllable implementation is bad
                                                            dispatch(changeQbooksLineItemUnitPrice(li.id, -1));
                                                            queueMicrotask(() => {
                                                                dispatch(
                                                                    changeQbooksLineItemUnitPrice(li.id, prevUnitPrice)
                                                                );
                                                            });
                                                        });
                                                    } else {
                                                        dispatch(changeQbooksLineItemUnitPrice(li.id, newValue));
                                                    }
                                                }}
                                                placeholder={messages.cellPlaceholderUnitPrice}
                                                theme='transparent'
                                                align='right'
                                                min={constants.qBooksConstants.MIN_AMOUNT}
                                                max={constants.qBooksConstants.MAX_AMOUNT}
                                                precision={8}
                                                displayFormat='currency'
                                                changeOnBlur={false}
                                            />

                                            <PriceIndicator $warningLevel={warningLevel} />
                                        </>
                                    )}
                                </FocusImplementer>
                            )}
                        </StyledPriceCheckIndicator>
                    );
                },
            },
            ...(request.details.lineAmountType !== domain.LineAmountType.NoTax
                ? [
                      {
                          id: 'taxCode',
                          name: messages.colNameTaxCode,
                          flexGrow: 0.7,
                          readonly: () => ocrIsLoading,
                          invalid: (li) =>
                              qbooksContext.isTrackingTaxesEnabled && showValidationErrors && !li.empty && !li.taxCode,
                          cell: (li, { invalid, readonly }) =>
                              readonly ? (
                                  <ReadOnlyCell text={li.taxCode && li.taxCode.text} />
                              ) : (
                                  <QbooksLineItemDropdownCellStyled
                                      qa={qa('cell-editor')}
                                      items={taxCodesList}
                                      value={li.taxCode || null}
                                      invalid={invalid}
                                      onChange={(newTax: domain.QBooksTaxCode | null) =>
                                          dispatch(changeQbooksLineItemsTaxCode([li.id], newTax))
                                      }
                                      placeholder={messages.cellPlaceholderTaxCode}
                                  />
                              ),
                      } as ColumnDefinition<ExpandedQBooksLineItem>,
                  ]
                : []),
            ...((isBill || isExpense) && qbooksContext.hasBillableFeature
                ? [
                      {
                          id: 'billable',
                          name: messages.colNameBillable,
                          fixedWidth: 75,
                          readonly: () => ocrIsLoading,
                          cell: (li) => (
                              <BillableCheckbox>
                                  <CheckboxEditor
                                      qa={qa('billable-checkbox')}
                                      value={!!li.isBillable}
                                      onChange={(newValue: boolean) =>
                                          dispatch(
                                              toggleQbooksLineItemIsBillable(
                                                  li.id,
                                                  qbooksContext.hasTaxableFeature,
                                                  newValue
                                              )
                                          )
                                      }
                                  />
                              </BillableCheckbox>
                          ),
                      } as ColumnDefinition<ExpandedQBooksLineItem>,
                  ]
                : []),
            ...((isBill || isExpense || isSalesInvoice) && qbooksContext.hasTaxableFeature
                ? [
                      {
                          id: 'tax',
                          name: messages.colNameTax,
                          fixedWidth: 45,
                          readonly: () => ocrIsLoading,
                          cell: (li) => (
                              <TaxableCheckbox>
                                  <CheckboxEditor
                                      qa={qa('taxable-checkbox')}
                                      value={!!li.isTaxable}
                                      onChange={(newValue: boolean) =>
                                          dispatch(toggleQbooksLineItemIsTaxable(li.id, newValue))
                                      }
                                  />
                              </TaxableCheckbox>
                          ),
                      } as ColumnDefinition<ExpandedQBooksLineItem>,
                  ]
                : []),
            ...(!isSalesInvoice && qbooksContext.hasCustomerFeature
                ? [
                      {
                          id: 'customer',
                          name: messages.colNameCustomer,
                          flexGrow: 1,
                          readonly: () => ocrIsLoading,
                          invalid: (li) => showValidationErrors && !li.empty && customerColMandatory && !li.customer,
                          cell: (li, { invalid, readonly }) =>
                              readonly ? (
                                  <ReadOnlyCell text={li.customer && li.customer.text} />
                              ) : (
                                  <QBooksLineItemCustomerCellStyled
                                      qa={qa('cell-editor')}
                                      value={li.customer || null}
                                      invalid={invalid}
                                      onChange={(newTax: Reference | null) =>
                                          dispatch(changeQbooksLineItemsCustomer([li.id], newTax))
                                      }
                                      placeholder={messages.cellPlaceholderCustomer}
                                  />
                              ),
                      } as ColumnDefinition<ExpandedQBooksLineItem>,
                  ]
                : []),
            ...(qbooksContext.hasClassFeature
                ? [
                      {
                          id: 'class',
                          name: messages.colNameClass,
                          flexGrow: 1,
                          readonly: () => ocrIsLoading,
                          invalid: (li) => showValidationErrors && !li.empty && classColMandatory && !li.class,
                          cell: (li, { invalid, readonly }) =>
                              readonly ? (
                                  <ReadOnlyCell text={li.class && li.class.text} />
                              ) : (
                                  <QbooksLineItemDropdownCellStyled
                                      qa={qa('cell-editor')}
                                      items={classes}
                                      value={li.class || null}
                                      invalid={invalid}
                                      onChange={(newTax: Reference | null) =>
                                          dispatch(changeQbooksLineItemsClass([li.id], newTax))
                                      }
                                      placeholder={messages.cellPlaceholderClass}
                                      listItem={(props) => (
                                          <DropdownEditor.ListItem
                                              {...props}
                                              nestingLevel={(props.item as QBooksClass).nestingLevel}
                                          />
                                      )}
                                  />
                              ),
                      } as ColumnDefinition<ExpandedQBooksLineItem>,
                  ]
                : []),
            {
                id: 'amount',
                name: isSalesInvoice
                    ? messages.colNameAmountWithCurrency({ currency: request.currency })
                    : messages.colNameAmount,
                flexGrow: isSalesInvoice ? 0.9 : 0.7,
                alignRight: true,
                readonly: () => true,
                invalid: () => false,
                cell: (li) => {
                    const amountText = li.computedAmount ? intl.formatNumber(li.computedAmount, 2) : undefined;

                    return (
                        <AmountCell data-qa={qa('cell-value')} title={amountText}>
                            {amountText}
                        </AmountCell>
                    );
                },
            },
            ...(showManualMatching && isBill
                ? [
                      {
                          id: 'linked',
                          name: messages.colNameLinked,
                          cell: (li) => {
                              if (isQBOMatchingOnEditingBetaSupported) {
                                  if (noMatchingPossible || !li.item?.id || !li.computedAmount) {
                                      return <span />;
                                  }

                                  // `onRemoveMatching` will be defined for isBill. this condition is only for
                                  // TS complain satisfaction
                                  if (typeof li.matchedLineItemId === 'string' && onRemoveMatching) {
                                      return (
                                          <ComponentErrorBoundary>
                                              <QBooksMatchingInfo
                                                  lineItemId={li.id}
                                                  profileId={profileId}
                                                  request={
                                                      request as Extract<
                                                          typeof request,
                                                          | selectors.types.QBooksBillExpandedRequest
                                                          | selectors.types.QBooksPoExpandedRequest
                                                      >
                                                  }
                                                  onRemoveMatching={onRemoveMatching}
                                                  matchingInfoNormalizer={matchingInfoNormalizer}
                                                  isNewLineItem={li.isNew}
                                              />
                                          </ComponentErrorBoundary>
                                      );
                                  }

                                  if (canDoMatching) {
                                      return (
                                          <NewButton icon noPadding onClick={() => setLineItem(li)}>
                                              <AddMatchingIcon />
                                          </NewButton>
                                      );
                                  }

                                  // remove this line after removing `isQBOMatchingOnEditingBetaSupported`
                                  // condition above
                                  return null;
                              }

                              return li.isMatched ? (
                                  <Linked>
                                      <MatchingIcon />
                                  </Linked>
                              ) : (
                                  <span />
                              );
                          },
                          fixedWidth: 60,
                      } as ColumnDefinition<ExpandedQBooksLineItem>,
                  ]
                : []),
        ];
    }, [
        request,
        qbooksContext.hasClassFeature,
        qbooksContext.classField,
        qbooksContext.hasCustomerFeature,
        qbooksContext.salesFormOptionalFields?.serviceDateEnabled,
        qbooksContext.hasBillableFeature,
        qbooksContext.hasTaxableFeature,
        qbooksContext.isTrackingTaxesEnabled,
        classes,
        requiredFields.fieldIds,
        context.fields,
        priceCheckerSettings?.isEnabledForBills,
        priceCheckerSettings?.isEnabledForExpenses,
        priceCheckerSettings?.isEnabledForPurchaseOrders,
        isSalesInvoice,
        isApproverMode,
        showManualMatching,
        dispatch,
        ocrIsLoading,
        showValidationErrors,
        onChange,
        isMultiCurrency,
        taxCodesList,
        isQBOMatchingOnEditingBetaSupported,
        noMatchingPossible,
        onRemoveMatching,
        canDoMatching,
        profileId,
        matchingInfoNormalizer,
        setLineItem,
    ]);
    const prevRequestDate = usePrevious(details.date);

    useUpdateEffect(() => {
        if (moment(prevRequestDate).diff(details.date) !== 0) {
            lineItems.forEach((lineItem) => {
                const isExistsTaxCode = taxCodesList.some((taxCode) => taxCode.id === lineItem.taxCode?.id);

                if (lineItem.taxCode && !isExistsTaxCode) {
                    dispatch(changeQbooksLineItemsTaxCode([lineItem.id], null));
                }
            });
        }
    }, [details.date, dispatch, lineItems, prevRequestDate, taxCodesList]);

    return (
        <Root
            qa={qa()}
            noPadding
            title={
                <Header>
                    <div>{messages.headerText}</div>

                    {qbooksContext.hasTaxesFeature && !isUSOrg && (
                        <HeaderFields>
                            <HeaderFieldsLabel>{messages.lineAmountTypeLabel}</HeaderFieldsLabel>

                            <HeaderFieldsLineAmountTypeContainer>
                                <PlainDataProvider items={lineAmountTypeReferences}>
                                    <DropdownEditor
                                        qa={qa('line-amount-type-editor')}
                                        value={findLineAmountType(request.details.lineAmountType)}
                                        onChange={(value: { id: domain.LineAmountType }) =>
                                            dispatch(changeQbooksLineAmountType(value.id))
                                        }
                                        buttonComponent={DropdownEditor.NoFilterButton}
                                    />
                                </PlainDataProvider>
                            </HeaderFieldsLineAmountTypeContainer>
                        </HeaderFields>
                    )}

                    {isShowGroupButtons && (
                        <GroupActions>
                            {isGroupEditVisible && (
                                <Button preset='small' execute={openGroupEdit}>
                                    {messages.groupEdit}
                                </Button>
                            )}

                            {isGroupCloneVisible && (
                                <Button preset='small' execute={handleGroupClone}>
                                    {messages.groupClone}
                                </Button>
                            )}

                            {isGroupRemoveVisible && (
                                <Button preset='small' execute={handleGroupRemove}>
                                    {messages.groupRemove}
                                </Button>
                            )}
                        </GroupActions>
                    )}
                </Header>
            }
        >
            <Content>
                <LineItemsTable
                    canAddLine
                    columnDefinitions={tableDef}
                    lineItems={lineItems}
                    invalid={showValidationErrors && lineItems.every((li) => li.empty) && !hasOtherLineItems}
                    onReorder={(oldIndex, newIndex) => dispatch(reorderQbooksLineItems(oldIndex, newIndex))}
                    onRemoveLine={(lineId) => dispatch(removeQbooksLineItems([lineId]))}
                    onCloneLine={(lineId) => dispatch(cloneQbooksLineItems([lineId]))}
                    onAddLine={() => dispatch(addQbooksLineItem())}
                />
            </Content>

            {isGroupEditPopupVisible && <LineItemsGroupEdit onClose={closeGroupEdit} items={groupItems} />}
        </Root>
    );
});

QBooksLineItemsSection.displayName = 'QBooksLineItemsSection';
